import { useState, useEffect, useCallback } from 'react';
import { ActiveTab, PolicyType } from '../types';

export interface RouteState {
  activeTab: ActiveTab;
  companyId: string;
  policyType: PolicyType;
  baseRevId: string | null;
  targetRevId: string | null;
  viewMode: 'timeline' | 'reader';
}

function parseLocation(): RouteState {
  if (typeof window === 'undefined') {
    return {
      activeTab: 'feed',
      companyId: 'anthropic',
      policyType: 'terms_of_service',
      baseRevId: null,
      targetRevId: null,
      viewMode: 'timeline',
    };
  }

  const pathname = window.location.pathname;
  const searchParams = new URLSearchParams(window.location.search);

  // 1. Check path-based routes: /company/:companyId/:policyType?
  const companyMatch = pathname.match(/^\/company\/([^\/]+)(?:\/([^\/]+))?(?:\/([^\/]+))?/);

  let activeTab: ActiveTab = 'feed';
  let companyId = 'anthropic';
  let policyType: PolicyType = 'terms_of_service';
  let viewMode: 'timeline' | 'reader' = 'timeline';
  let baseRevId: string | null = null;
  let targetRevId: string | null = null;

  if (companyMatch) {
    activeTab = 'timeline';
    companyId = companyMatch[1];
    if (companyMatch[2]) {
      policyType = companyMatch[2] as PolicyType;
    }
    if (companyMatch[3] === 'read' || companyMatch[3] === 'reader') {
      viewMode = 'reader';
    }
  } else if (pathname === '/companies' || pathname === '/catalog') {
    activeTab = 'catalog';
  } else if (pathname === '/admin') {
    activeTab = 'admin';
  } else if (pathname === '/feed' || pathname === '/') {
    activeTab = 'feed';
  }

  // 2. Query param overrides (support ?company=...&policy=...&view=...&base=...&target=...)
  const qTab = searchParams.get('tab');
  if (qTab && ['feed', 'catalog', 'timeline', 'admin'].includes(qTab)) {
    activeTab = qTab as ActiveTab;
  }

  const qCompany = searchParams.get('company');
  if (qCompany) {
    companyId = qCompany;
    if (!qTab && pathname === '/') {
      activeTab = 'timeline';
    }
  }

  const qPolicy = searchParams.get('policy');
  if (qPolicy) {
    policyType = qPolicy as PolicyType;
  }

  const qView = searchParams.get('view');
  if (qView === 'reader' || qView === 'read') {
    viewMode = 'reader';
  } else if (qView === 'timeline') {
    viewMode = 'timeline';
  }

  baseRevId = searchParams.get('base');
  targetRevId = searchParams.get('target');

  return {
    activeTab,
    companyId,
    policyType,
    baseRevId,
    targetRevId,
    viewMode,
  };
}

function buildUrl(state: RouteState): string {
  const { activeTab, companyId, policyType, baseRevId, targetRevId, viewMode } = state;

  if (activeTab === 'feed') {
    return '/';
  }
  if (activeTab === 'catalog') {
    return '/companies';
  }
  if (activeTab === 'admin') {
    return '/admin';
  }

  // Timeline / Reader / Diff
  const params = new URLSearchParams();
  if (viewMode === 'reader') {
    params.set('view', 'reader');
  }
  if (baseRevId && targetRevId) {
    params.set('base', baseRevId);
    params.set('target', targetRevId);
  }

  const queryString = params.toString() ? `?${params.toString()}` : '';
  return `/company/${companyId}/${policyType}${queryString}`;
}

export function useUrlRouter() {
  const [route, setRoute] = useState<RouteState>(parseLocation);

  // Sync state with browser back/forward buttons
  useEffect(() => {
    const handlePopState = () => {
      setRoute(parseLocation());
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const navigate = useCallback((nextState: Partial<RouteState>, replace: boolean = false) => {
    setRoute((prev) => {
      const updated: RouteState = { ...prev, ...nextState };
      const newUrl = buildUrl(updated);

      if (typeof window !== 'undefined') {
        const currentFullUrl = window.location.pathname + window.location.search;
        if (currentFullUrl !== newUrl) {
          if (replace) {
            window.history.replaceState(updated, '', newUrl);
          } else {
            window.history.pushState(updated, '', newUrl);
          }
        }
      }
      return updated;
    });
  }, []);

  const navigateToFeed = useCallback(() => {
    navigate({ activeTab: 'feed', baseRevId: null, targetRevId: null });
  }, [navigate]);

  const navigateToCatalog = useCallback(() => {
    navigate({ activeTab: 'catalog', baseRevId: null, targetRevId: null });
  }, [navigate]);

  const navigateToAdmin = useCallback(() => {
    navigate({ activeTab: 'admin', baseRevId: null, targetRevId: null });
  }, [navigate]);

  const navigateToCompany = useCallback(
    (
      companyId: string,
      policyType: PolicyType = 'terms_of_service',
      viewMode: 'timeline' | 'reader' = 'timeline',
      baseRev?: string | null,
      targetRev?: string | null
    ) => {
      navigate({
        activeTab: 'timeline',
        companyId,
        policyType,
        viewMode,
        baseRevId: baseRev || null,
        targetRevId: targetRev || null,
      });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    [navigate]
  );

  return {
    ...route,
    navigate,
    navigateToFeed,
    navigateToCatalog,
    navigateToAdmin,
    navigateToCompany,
  };
}
